import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@haltongroup/halton-ui-library';
import { AppSettings, CountryCode, PrintLocale } from '../types';
import { printLanguages, countries, defaultSettings } from '../config';
import { createDemoOffer, createDemoWorkOrder, createDemoMaintenanceReport, exportDocuments, importDocuments, loadWorkOrders, loadOffers, getNextJobNumber, loadCounters, saveCounters } from '../utils';
import {
  REFERENCE_SNAPSHOT_SHAREPOINT_URL,
  clearReferenceSnapshot,
  importReferenceSnapshotFile,
  loadReferenceSnapshot,
} from '../utils/referenceSnapshot';
import { importWoReturnFile } from '../utils/woReturn';
import AirflowPattern from './AirflowPattern';

interface SettingsProps {
  settings: AppSettings;
  onUpdateSettings: (newSettings: Partial<AppSettings>) => void;
  onResetData?: () => void;
}

const Settings = ({ settings, onUpdateSettings, onResetData }: SettingsProps) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<'settings' | 'guide'>('settings');
  const [importMessage, setImportMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [referenceSnapshot, setReferenceSnapshot] = useState(() => loadReferenceSnapshot());
  const [referenceMessage, setReferenceMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [woReturnMessage, setWoReturnMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleChange = (key: keyof AppSettings, value: string | number | boolean) => {
    onUpdateSettings({ [key]: value });
  };

  const handleReset = () => {
    if (window.confirm(t('settings.confirmReset'))) {
      onUpdateSettings(defaultSettings);
      if (onResetData) onResetData();
    }
  };

  const handleCreateDemoOffer = () => {
    createDemoOffer();
    alert(t('settings.data.demoOfferCreated') || 'Demo offer created! Check the Archive.');
    window.location.reload();
  };

  const handleCreateDemoWorkOrder = () => {
    createDemoWorkOrder();
    alert(t('settings.data.demoWorkOrderCreated') || 'Demo work order created! Check the Archive.');
    window.location.reload();
  };

  const handleCreateDemoMaintenanceReport = () => {
    createDemoMaintenanceReport();
    alert(t('settings.data.demoMaintenanceReportCreated') || 'Demo maintenance report created! Check the Archive.');
    window.location.reload();
  };

  const handleExportAll = () => {
    const workOrders = loadWorkOrders();
    const offers = loadOffers();
    if (workOrders.length === 0 && offers.length === 0) {
      alert(t('settings.data.noDataToExport') || 'No data to export. Create some quotes or work orders first.');
      return;
    }
    exportDocuments(workOrders, offers);
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const result = await importDocuments(file);
    if (result.success) {
      setImportMessage({
        type: 'success',
        text: t('archive.importSuccess', { workOrders: result.workOrdersImported, offers: result.offersImported, ansulCommissionings: result.ansulCommissioningsImported })
      });
      setTimeout(() => {
        setImportMessage(null);
        window.location.reload();
      }, 2000);
    } else {
      setImportMessage({ type: 'error', text: result.errors.join(', ') || 'Import failed' });
      setTimeout(() => setImportMessage(null), 5000);
    }
    event.target.value = '';
  };

  const handleImportReferenceSnapshot = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const result = await importReferenceSnapshotFile(file);
    if (result.success) {
      setReferenceSnapshot(result.snapshot);
      setReferenceMessage({
        type: 'success',
        text: t('referenceData.settings.importSuccess', {
          accounts: result.snapshot.counts.accounts,
          sites: result.snapshot.counts.sites,
          date: new Date(result.snapshot.generatedAt).toLocaleDateString('fi-FI'),
        }),
      });
      // Notify open pickers that fresh data is available
      window.dispatchEvent(new Event('halton-reference-snapshot-updated'));
    } else {
      setReferenceMessage({
        type: 'error',
        text: t('referenceData.settings.importError', { error: result.error }),
      });
    }
    setTimeout(() => setReferenceMessage(null), 6000);
    event.target.value = '';
  };

  const handleClearReferenceSnapshot = () => {
    clearReferenceSnapshot();
    setReferenceSnapshot(null);
    window.dispatchEvent(new Event('halton-reference-snapshot-updated'));
  };

  const handleImportWoReturn = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const outcome = await importWoReturnFile(file);
    if (outcome.success) {
      setWoReturnMessage({
        type: 'success',
        text: t('referenceData.woReturn.importSuccess', {
          applied: outcome.result.applied,
          alreadySet: outcome.result.alreadySet,
          unmatched: outcome.result.unmatched.length,
        }),
      });
    } else {
      setWoReturnMessage({
        type: 'error',
        text: t('referenceData.woReturn.importError', { error: outcome.error }),
      });
    }
    setTimeout(() => setWoReturnMessage(null), 8000);
    event.target.value = '';
  };

  const handleClearAllData = () => {
    if (window.confirm(t('settings.data.confirmClearAll') || 'Are you sure you want to delete ALL saved data? This cannot be undone.')) {
      if (window.confirm(t('settings.data.confirmClearAllSecond') || 'This will permanently delete all quotes, work orders, and maintenance reports. Are you absolutely sure?')) {
        localStorage.removeItem('halton_offers');
        localStorage.removeItem('halton_work_orders');
        localStorage.removeItem('halton_maintenance_reports');
        localStorage.removeItem('halton_counters');
        alert(t('settings.data.dataCleared') || 'All data has been cleared.');
        window.location.reload();
      }
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-hero">
        <AirflowPattern variant="light" />
        <div className="settings-hero-content">
          <h1>{t('settings.title')}</h1>
          <p className="page-subtitle">{t('settings.subtitle')}</p>
        </div>
      </div>

      {/* Mascot Overlay */}
      <div className="settings-mascot-overlay">
        <img src="/service-hero.png" alt="Halton Service Hero" />
      </div>

      {/* Tab Navigation */}
      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeSection === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveSection('settings')}
        >
          ⚙️ {t('settings.tabs.settings') || 'Settings'}
        </button>
        <button
          className={`settings-tab ${activeSection === 'guide' ? 'active' : ''}`}
          onClick={() => setActiveSection('guide')}
        >
          📖 {t('settings.tabs.userGuide') || 'User Guide'}
        </button>
      </div>

      {activeSection === 'settings' && (
        <div className="settings-grid">
          {/* User Profile */}
          <section className="form-section">
            <div className="section-header-with-icon">
              <span className="section-icon">👤</span>
              <h2 className="section-title">{t('settings.userProfile.title') || 'User Profile'}</h2>
            </div>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              {t('settings.userProfile.description') || 'Personal details used for quote numbers and future quote print signatures.'}
            </p>

            <div className="form-group">
              <label htmlFor="userFullName">{t('settings.userProfile.fullName') || 'Full name'}</label>
              <input
                id="userFullName"
                type="text"
                value={settings.userFullName || ''}
                onChange={(e) => handleChange('userFullName', e.target.value)}
                placeholder={t('settings.userProfile.fullNamePlaceholder') || 'Aku Sveins'}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="userSignature">{t('settings.userProfile.signature') || 'Signature'}</label>
              <input
                id="userSignature"
                type="text"
                value={settings.userSignature || ''}
                onChange={(e) => handleChange('userSignature', e.target.value)}
                placeholder={t('settings.userProfile.signaturePlaceholder') || 'Aku Sveins, Service'}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="userInitials">{t('settings.userProfile.quoteInitials') || 'Quote initials'}</label>
              <input
                id="userInitials"
                type="text"
                value={settings.userInitials || ''}
                onChange={(e) => handleChange('userInitials', e.target.value.replace(/[^a-zåäöA-ZÅÄÖ]/g, '').toUpperCase().slice(0, 4))}
                placeholder="AS"
                className="form-input"
                maxLength={4}
              />
              <small className="form-hint">{t('settings.userProfile.quoteInitialsHint') || 'Used in quote numbers, for example Q-AS-230626-01.'}</small>
            </div>
          </section>

          {/* Company Settings */}
          <section className="form-section">
            <div className="section-header-with-icon">
              <span className="section-icon">🏢</span>
              <h2 className="section-title">{t('settings.company.title')}</h2>
            </div>

            <div className="form-group">
              <label>{t('settings.company.country')}</label>
              <select
                value={settings.companyCountry}
                onChange={(e) => handleChange('companyCountry', e.target.value as CountryCode)}
                className="form-input"
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
              <small className="form-hint">{t('settings.company.countryHint')}</small>
            </div>
          </section>

          {/* Print Settings */}
          <section className="form-section">
            <div className="section-header-with-icon">
              <span className="section-icon">📄</span>
              <h2 className="section-title">{t('settings.print.title')}</h2>
            </div>

            <div className="form-group">
              <label>{t('settings.print.language')}</label>
              <select
                value={settings.printLanguage}
                onChange={(e) => handleChange('printLanguage', e.target.value as PrintLocale)}
                className="form-input"
              >
                {printLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name} ({lang.nativeName})
                  </option>
                ))}
              </select>
              <small className="form-hint">{t('settings.print.languageHint')}</small>
            </div>

            <div className="form-group">
              <label>{t('settings.print.paymentTerms')}</label>
              <input
                type="number"
                value={settings.paymentTermsDays}
                onChange={(e) => handleChange('paymentTermsDays', parseInt(e.target.value) || 14)}
                className="form-input"
                min={1}
                max={90}
              />
              <small className="form-hint">{t('settings.print.paymentTermsHint')}</small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.showLogo}
                  onChange={(e) => handleChange('showLogo', e.target.checked)}
                />
                <span>{t('settings.print.showLogo')}</span>
              </label>
            </div>
          </section>

          {/* Default Rates */}
          <section className="form-section">
            <div className="section-header-with-icon">
              <span className="section-icon">💰</span>
              <h2 className="section-title">{t('settings.rates.title')}</h2>
            </div>

            <div className="form-grid-3">
              <div className="form-group">
                <label>{t('settings.rates.hourlyRate')}</label>
                <input
                  type="number"
                  value={settings.defaultHourlyRate}
                  onChange={(e) => handleChange('defaultHourlyRate', parseFloat(e.target.value) || 85)}
                  className="form-input"
                  min={0}
                  step={5}
                />
              </div>

              <div className="form-group">
                <label>{t('settings.rates.distanceRate')}</label>
                <input
                  type="number"
                  value={settings.defaultDistanceRate}
                  onChange={(e) => handleChange('defaultDistanceRate', parseFloat(e.target.value) || 0.75)}
                  className="form-input"
                  min={0}
                  step={0.05}
                />
              </div>

              <div className="form-group">
                <label>{t('settings.rates.timeRate')}</label>
                <input
                  type="number"
                  value={settings.defaultTimeRate}
                  onChange={(e) => handleChange('defaultTimeRate', parseFloat(e.target.value) || 45)}
                  className="form-input"
                  min={0}
                  step={5}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-group">
                <label>{t('settings.rates.accommodation')}</label>
                <input
                  type="number"
                  value={settings.accommodationRate}
                  onChange={(e) => handleChange('accommodationRate', parseFloat(e.target.value) || 135)}
                  className="form-input"
                  min={0}
                  step={5}
                />
              </div>

              <div className="form-group">
                <label>{t('settings.rates.perDiem')}</label>
                <input
                  type="number"
                  value={settings.perDiemRate}
                  onChange={(e) => handleChange('perDiemRate', parseFloat(e.target.value) || 51)}
                  className="form-input"
                  min={0}
                  step={1}
                />
              </div>
            </div>
          </section>

          {/* Job Number Settings */}
          <section className="form-section form-section-prominent">
            <div className="section-header-with-icon">
              <span className="section-icon">🔢</span>
              <h2 className="section-title">{t('settings.jobNumber.title') || 'Job Number Settings'}</h2>
            </div>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              {t('settings.jobNumber.format') || 'Format: XYY-NNN (X=user, YY=year, NNN=sequence)'}
            </p>

            <div className="form-grid-3">
              <div className="form-group">
                <label>{t('settings.jobNumber.userIdentity') || 'User Identity Number'}</label>
                <select
                  value={settings.userIdentityNumber}
                  onChange={(e) => handleChange('userIdentityNumber', parseInt(e.target.value))}
                  className="form-input"
                  style={{ fontSize: '1.125rem', fontWeight: 600 }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <small className="form-hint">{t('settings.jobNumber.userIdentityHint') || 'Your personal identifier (1-9) used in job numbers'}</small>
              </div>

              <div className="form-group">
                <label>{t('settings.jobNumber.counter') || 'Current Job Counter'}</label>
                <input
                  type="number"
                  value={loadCounters().jobNumberCounter}
                  onChange={(e) => {
                    const counters = loadCounters();
                    counters.jobNumberCounter = Math.max(1, Math.min(999, parseInt(e.target.value) || 1));
                    saveCounters(counters);
                    // Force re-render by updating any setting
                    handleChange('userIdentityNumber', settings.userIdentityNumber);
                  }}
                  className="form-input input-job-number"
                  min={1}
                  max={999}
                />
                <small className="form-hint">{t('settings.jobNumber.counterHint') || 'Next job will use this number'}</small>
              </div>

              <div className="form-group">
                <label>{t('settings.jobNumber.preview') || 'Next Job Number'}</label>
                <div className="job-number-preview">
                  {getNextJobNumber(settings.userIdentityNumber)}
                </div>
              </div>
            </div>
          </section>

          {/* Service Branch Location */}
          <section className="form-section">
            <div className="section-header-with-icon">
              <span className="section-icon">📍</span>
              <h2 className="section-title">{t('settings.serviceBranch.title') || 'Service Branch Location'}</h2>
            </div>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              {t('settings.serviceBranch.description') || 'Set your service branch address. This will be used as the starting point for automatic travel distance calculations.'}
            </p>

            <div className="form-group">
              <label>{t('settings.serviceBranch.name') || 'Branch Name'}</label>
              <input
                type="text"
                value={settings.serviceBranchName || ''}
                onChange={(e) => handleChange('serviceBranchName', e.target.value)}
                placeholder={t('settings.serviceBranch.namePlaceholder') || 'e.g., Helsinki Service Center'}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t('settings.serviceBranch.address') || 'Branch Address'}</label>
              <input
                type="text"
                value={settings.serviceBranchAddress || ''}
                onChange={(e) => handleChange('serviceBranchAddress', e.target.value)}
                placeholder={t('settings.serviceBranch.addressPlaceholder') || 'e.g., Yrittäjäntie 15, 01800 Klaukkala, Finland'}
                className="form-input"
              />
              <small className="form-hint">{t('settings.serviceBranch.addressHint') || 'Enter full address including city and country for accurate distance calculations'}</small>
            </div>

            {/* Google Maps API Section */}
            <div className="settings-subsection" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'var(--halton-gray-100)', borderRadius: '8px' }}>
              <h3 className="subsection-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🗺️ {t('settings.serviceBranch.googleMaps.title') || 'Google Maps Integration'}
              </h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                {t('settings.serviceBranch.googleMaps.description') || 'Enable address autocomplete and automatic travel distance calculation from your branch to service sites.'}
              </p>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {t('settings.serviceBranch.googleMaps.apiKey') || 'Google Maps API Key'}
                  {settings.googleMapsApiKey && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--halton-accent-green)', fontWeight: 500 }}>
                      ✓ {t('common.saved') || 'Saved'}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={settings.googleMapsApiKey || ''}
                  onChange={(e) => handleChange('googleMapsApiKey', e.target.value)}
                  placeholder={t('settings.serviceBranch.googleMaps.apiKeyPlaceholder') || 'Enter your Google Maps API key'}
                  className="form-input"
                />
                <small className="form-hint">
                  {t('settings.serviceBranch.googleMaps.apiKeyHint') || 'Get your API key from Google Cloud Console. Requires Maps JavaScript API and Distance Matrix API enabled.'}
                </small>
              </div>

              <div className="info-box" style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--primary-light)', borderRadius: '6px', fontSize: '0.85rem' }}>
                <strong>✨ {t('settings.serviceBranch.googleMaps.features') || 'When enabled, you will be able to:'}</strong>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                  <li>{t('settings.serviceBranch.googleMaps.feature1') || 'Auto-complete addresses as you type'}</li>
                  <li>{t('settings.serviceBranch.googleMaps.feature2') || 'Calculate travel distance automatically from branch to service site'}</li>
                  <li>{t('settings.serviceBranch.googleMaps.feature3') || 'Choose one-way or round-trip for travel cost calculation'}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Reference Data (master snapshot, F2) */}
          <section className="form-section" data-testid="reference-data-settings">
            <div className="section-header-with-icon">
              <span className="section-icon">🗂️</span>
              <h2 className="section-title">{t('referenceData.settings.title')}</h2>
            </div>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              {t('referenceData.settings.description')}{' '}
              <a href={REFERENCE_SNAPSHOT_SHAREPOINT_URL} target="_blank" rel="noreferrer">
                {t('referenceData.downloadLink')}
              </a>
            </p>
            <p className="form-hint" data-testid="reference-snapshot-status">
              {referenceSnapshot
                ? t('referenceData.settings.currentStatus', {
                    accounts: referenceSnapshot.counts.accounts,
                    sites: referenceSnapshot.counts.sites,
                    date: new Date(referenceSnapshot.generatedAt).toLocaleDateString('fi-FI'),
                  })
                : t('referenceData.settings.noSnapshot')}
            </p>
            {referenceSnapshot && referenceSnapshot.warnings.length > 0 && (
              <details style={{ marginBottom: '0.75rem' }}>
                <summary className="form-hint">{t('referenceData.settings.warningsTitle')} ({referenceSnapshot.warnings.length})</summary>
                <ul className="form-hint">
                  {referenceSnapshot.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </details>
            )}
            <div className="button-group" style={{ gap: '0.5rem' }}>
              <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                🗂️ {t('referenceData.settings.importButton')}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportReferenceSnapshot}
                  style={{ display: 'none' }}
                  data-testid="reference-snapshot-file-input"
                />
              </label>
              {referenceSnapshot && (
                <Button
                  theme="secondary"
                  label={`🗑️ ${t('referenceData.settings.clearButton')}`}
                  onClick={handleClearReferenceSnapshot}
                />
              )}
            </div>
            {referenceMessage && (
              <div className={`import-result ${referenceMessage.type}`} style={{ marginTop: '1rem' }} data-testid="reference-snapshot-message">
                {referenceMessage.type === 'success' ? '✅' : '❌'} {referenceMessage.text}
              </div>
            )}

            {/* Master WO number return import (F6) */}
            <div className="settings-subsection" data-testid="wo-return-settings">
              <h3 className="subsection-title">{t('referenceData.woReturn.title')}</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                {t('referenceData.woReturn.description')}
              </p>
              <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                🔁 {t('referenceData.woReturn.importButton')}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportWoReturn}
                  style={{ display: 'none' }}
                  data-testid="wo-return-file-input"
                />
              </label>
              {woReturnMessage && (
                <div className={`import-result ${woReturnMessage.type}`} style={{ marginTop: '1rem' }} data-testid="wo-return-message">
                  {woReturnMessage.type === 'success' ? '✅' : '❌'} {woReturnMessage.text}
                </div>
              )}
            </div>
          </section>

          {/* Data Management */}
          <section className="form-section">
            <div className="section-header-with-icon">
              <span className="section-icon">💾</span>
              <h2 className="section-title">{t('settings.data.title')}</h2>
            </div>

            {/* Export/Import Section */}
            <div className="settings-subsection">
              <h3 className="subsection-title">{t('settings.data.exportImport') || 'Export & Import'}</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                {t('settings.data.exportImportDescription') || 'Share your quotes and work orders with colleagues or backup your data.'}
              </p>
              <div className="button-group" style={{ gap: '0.5rem' }}>
                <Button theme="secondary" label={`📤 ${t('settings.data.exportAll') || 'Export All Data'}`} onClick={handleExportAll} />
                <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                  📥 {t('settings.data.importData') || 'Import Data'}
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImport}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              {importMessage && (
                <div className={`import-result ${importMessage.type}`} style={{ marginTop: '1rem' }}>
                  {importMessage.type === 'success' ? '✅' : '❌'} {importMessage.text}
                </div>
              )}
            </div>

            {/* Demo Data Section */}
            <div className="settings-subsection">
              <h3 className="subsection-title">{t('settings.data.demoData') || 'Demo Data for Testing'}</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                {t('settings.data.demoDataDescription') || 'Create sample offers and work orders with pre-filled data for testing purposes.'}
              </p>
              <div className="button-group" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <Button theme="tertiary" label={`📊 ${t('settings.data.createDemoOffer') || 'Create Demo Offer'}`} onClick={handleCreateDemoOffer} />
                <Button theme="tertiary" label={`📋 ${t('settings.data.createDemoWorkOrder') || 'Create Demo Work Order'}`} onClick={handleCreateDemoWorkOrder} />
                <Button theme="tertiary" label={`📝 ${t('settings.data.createDemoMaintenanceReport') || 'Create Demo Maintenance Report'}`} onClick={handleCreateDemoMaintenanceReport} />
              </div>
            </div>

            {/* Reset Section */}
            <div className="settings-subsection danger-zone">
              <h3 className="subsection-title" style={{ color: 'var(--halton-red)' }}>
                ⚠️ {t('settings.data.dangerZone') || 'Danger Zone'}
              </h3>
              <div className="button-group" style={{ gap: '0.5rem' }}>
                <Button theme="secondary" label={`🔄 ${t('settings.data.resetDefaults')}`} onClick={handleReset} />
                <Button theme="danger" label={`🗑️ ${t('settings.data.clearAllData') || 'Clear All Data'}`} onClick={handleClearAllData} />
              </div>
              <p className="form-hint" style={{ marginTop: '0.5rem' }}>
                {t('settings.data.storageNote')}
              </p>
            </div>
          </section>
        </div>
      )}

      {activeSection === 'guide' && (
        <div className="user-guide">
          {/* Getting Started */}
          <section className="guide-section">
            <div className="section-header-with-icon">
              <span className="section-icon">🚀</span>
              <h2 className="section-title">{t('settings.guide.gettingStarted.title') || 'Getting Started'}</h2>
            </div>
            <p className="guide-intro">
              {t('settings.guide.gettingStarted.intro') || 
                'Welcome to the Halton Service Tool! This application helps you create professional quotes, work orders, and maintenance reports for Halton service operations.'}
            </p>
          </section>

          {/* Workflow Overview */}
          <section className="guide-section">
            <div className="section-header-with-icon">
              <span className="section-icon">🔄</span>
              <h2 className="section-title">{t('settings.guide.workflow.title') || 'Typical Workflow'}</h2>
            </div>
            
            <div className="workflow-steps">
              <div className="workflow-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>{t('settings.guide.workflow.step1.title') || 'Create a Quote'}</h4>
                  <p>{t('settings.guide.workflow.step1.description') || 
                    'Use the Calculator to create a cost estimate. Add travel costs, labor hours, parts, and any additional charges. Fill in customer details and save as a quote.'}</p>
                </div>
              </div>

              <div className="workflow-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>{t('settings.guide.workflow.step2.title') || 'Convert to Work Order'}</h4>
                  <p>{t('settings.guide.workflow.step2.description') || 
                    'Once the quote is approved, go to Archive and click "Create Work Order" on the quote. This transfers all customer info and pricing to a new work order.'}</p>
                </div>
              </div>

              <div className="workflow-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>{t('settings.guide.workflow.step3.title') || 'Complete Work Order'}</h4>
                  <p>{t('settings.guide.workflow.step3.description') || 
                    'Fill in additional work order details like site information, work description, job number, and scheduling. Add technician information.'}</p>
                </div>
              </div>

              <div className="workflow-step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h4>{t('settings.guide.workflow.step4.title') || 'Print & Share'}</h4>
                  <p>{t('settings.guide.workflow.step4.description') || 
                    'Preview documents, select the print language, and use "Save as PDF" (not Microsoft Print to PDF) for selectable text in PDFs. Export documents to share with colleagues.'}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Features Guide */}
          <section className="guide-section">
            <div className="section-header-with-icon">
              <span className="section-icon">📋</span>
              <h2 className="section-title">{t('settings.guide.features.title') || 'Key Features'}</h2>
            </div>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <h4>{t('settings.guide.features.calculator.title') || 'Calculator'}</h4>
                <p>{t('settings.guide.features.calculator.description') || 
                  'Create cost estimates with automatic calculations for travel, labor, surcharges, and VAT. Supports multiple travel zones and night/weekend surcharges.'}</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📋</div>
                <h4>{t('settings.guide.features.workOrder.title') || 'Work Order'}</h4>
                <p>{t('settings.guide.features.workOrder.description') || 
                  'Professional work order documents with complete billing and site information, work descriptions, and signature fields.'}</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🔧</div>
                <h4>{t('settings.guide.features.maintenance.title') || 'Maintenance Reports'}</h4>
                <p>{t('settings.guide.features.maintenance.description') || 
                  'Detailed maintenance checklists for different equipment types including KCW, UV systems, Ansul R102, and M.A.R.V.E.L.'}</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🗄️</div>
                <h4>{t('settings.guide.features.archive.title') || 'Archive'}</h4>
                <p>{t('settings.guide.features.archive.description') || 
                  'Access all saved documents. Edit, copy, print, or delete. Export to Excel for reporting or JSON for sharing with colleagues.'}</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🌍</div>
                <h4>{t('settings.guide.features.multilingual.title') || 'Multi-Language Support'}</h4>
                <p>{t('settings.guide.features.multilingual.description') || 
                  'Print documents in 9 languages (Finnish, English, Swedish, Norwegian, Danish, German, French, Dutch, Polish) regardless of UI language.'}</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📤</div>
                <h4>{t('settings.guide.features.sharing.title') || 'Document Sharing'}</h4>
                <p>{t('settings.guide.features.sharing.description') || 
                  'Export quotes and work orders as JSON files to share with colleagues. Import their documents to edit and continue work.'}</p>
              </div>
            </div>
          </section>

          {/* Tips & Tricks */}
          <section className="guide-section">
            <div className="section-header-with-icon">
              <span className="section-icon">💡</span>
              <h2 className="section-title">{t('settings.guide.tips.title') || 'Tips & Best Practices'}</h2>
            </div>

            <ul className="tips-list">
              <li>
                <strong>{t('settings.guide.tips.pdf.title') || 'PDF Generation:'}</strong> {t('settings.guide.tips.pdf.description') || 
                  'Always use browser\'s "Save as PDF" option instead of "Microsoft Print to PDF" to ensure text is selectable in the generated PDF.'}
              </li>
              <li>
                <strong>{t('settings.guide.tips.jobNumber.title') || 'Job Numbers:'}</strong> {t('settings.guide.tips.jobNumber.description') || 
                  'Use consistent job number format (e.g., 126-001) where the first digit is user ID, next two are year, and last three are sequential number.'}
              </li>
              <li>
                <strong>{t('settings.guide.tips.backup.title') || 'Regular Backups:'}</strong> {t('settings.guide.tips.backup.description') || 
                  'Data is stored in your browser. Use "Export All Data" regularly to backup your work. Different browsers/devices have separate data.'}
              </li>
              <li>
                <strong>{t('settings.guide.tips.copy.title') || 'Copy Documents:'}</strong> {t('settings.guide.tips.copy.description') || 
                  'Use the Copy function in Archive to duplicate quotes or work orders as templates for similar jobs.'}
              </li>
              <li>
                <strong>{t('settings.guide.tips.printLanguage.title') || 'Print Language:'}</strong> {t('settings.guide.tips.printLanguage.description') || 
                  'You can change the document language in the print preview toolbar. This doesn\'t affect the UI language.'}
              </li>
            </ul>
          </section>

          {/* Info Boxes */}
          <section className="guide-section">
            <div className="section-header-with-icon">
              <span className="section-icon">ℹ️</span>
              <h2 className="section-title">{t('settings.guide.info.title') || 'Useful Information'}</h2>
            </div>

            <div className="info-box">
              <h4>{t('settings.guide.info.storage.title') || 'Data Storage'}</h4>
              <p>{t('settings.guide.info.storage.description') || 
                'All data is stored locally in your browser using localStorage. Data is not synced between browsers or devices. Export your data regularly for backup.'}</p>
            </div>

            <div className="info-box">
              <h4>{t('settings.guide.info.support.title') || 'Need Help?'}</h4>
              <p>{t('settings.guide.info.support.description') || 
                'Contact your local Halton service team for support or feature requests. This tool is continuously being improved based on user feedback.'}</p>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default Settings;
